/**
 * InstructionExecutor — Server-Decided Orchestration
 *
 * Replaces OrchestrationEngine, CampaignSelector, ChannelSelector,
 * MessageSelector, CampaignMemoryManager, and FrequencyEnforcer.
 *
 * The server pre-computes trigger-indexed render instructions.
 * This class is a simple lookup: trigger fires → get instruction → done.
 */

export interface RenderInstruction {
  campaignId: string;
  nodeId: string;
  stepIndex: number;
  trigger: {
    type: string;
    config?: Record<string, unknown>;
  };
  render: {
    type: 'onsite/popup' | 'onsite/notification';
    message: unknown;
    config?: unknown;
  };
  /** Protected pages this campaign chose to show on anyway. Absent: none. */
  pagePolicy?: { allowProtected: string[] };
}

import type { PublicWebPushOptInConfig } from '../types/sdk.types';

export interface InitResponse {
  instructions: Record<string, RenderInstruction | null>;
  storeInfo: {
    id: string;
    name: string;
    platform: string;
    timezone: string;
  };
  variables?: {
    context: Record<string, unknown>;
    values: Record<string, unknown>;
    timestamp: number;
    ttl: number;
  };
  /**
   * Facts about THIS visitor, decided server-side. `country` is the edge's
   * geolocation, and it is what a capture form's phone field starts on — see
   * bindCanvasPhones.
   */
  visitor?: {
    country: string | null;
  };
  sessionLimits: {
    maxOnSiteMessages: number;
    /** Added with PopupReferee; an older server omits them and the SDK uses ONSITE_POLICY. */
    popupsPerPageView?: number;
    popupCooldownMs?: number;
  };
  webPushOptIn?: PublicWebPushOptInConfig;
}

/**
 * Where the session's impression tally lives.
 *
 * `sessionStorage`, deliberately — not memory, and not the server.
 *
 * In memory it died on every navigation, so "2 messages per SESSION" was
 * really "2 per page load": a shopper browsing five pages could be shown the
 * same popup five times. sessionStorage survives navigation, clears itself
 * when the tab closes, and costs nothing to read.
 *
 * Not the server, because this is on the hot path. A trigger like exit intent
 * has a few hundred milliseconds to exist at all, and awaiting a round trip
 * before rendering either misses the moment or forces a bad default when the
 * request fails — show anyway (duplicates) or stay quiet (never appears).
 * Longer-horizon capping ("not this campaign again for 14 days") does belong
 * server-side; it needs to outlive the tab, which this cannot.
 */
const SESSION_KEY = 'hamla_shown_v1';

interface ShownState {
  keys: string[];
  count: number;
}

function readShownState(): ShownState {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return { keys: [], count: 0 };
    const parsed = JSON.parse(raw) as Partial<ShownState>;
    return {
      keys: Array.isArray(parsed.keys) ? parsed.keys : [],
      count: typeof parsed.count === 'number' ? parsed.count : 0,
    };
  } catch {
    // Private mode, disabled storage, or corrupt JSON. Fail OPEN: showing a
    // popup twice is a smaller harm than a storage quirk silencing every
    // campaign on the site.
    return { keys: [], count: 0 };
  }
}

function writeShownState(state: ShownState): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable — the in-memory copy still guards this page.
  }
}

export class InstructionExecutor {
  private instructions: Record<string, RenderInstruction | null>;
  private shown: Set<string>;
  private shownCount: number;
  private maxMessages: number;

  constructor(response: InitResponse) {
    this.instructions = response.instructions;
    this.maxMessages = response.sessionLimits.maxOnSiteMessages;

    // Seeded from the session so a navigation does not hand the shopper a
    // clean slate. The in-memory copies stay as the synchronous fast path.
    const restored = readShownState();
    this.shown = new Set(restored.keys);
    this.shownCount = restored.count;
  }

  /**
   * The step explicitly asked to re-show on EVERY trigger fire.
   *
   * The server sends the step's frequency rule along with the instruction,
   * and 'unlimited' is the author saying "every time the trigger works" in
   * the builder. The session 'shown' suppression exists to stop accidental
   * repeats — it must not outrank an explicit rule, or the setting shows
   * once per tab and looks broken until the shopper clears storage.
   */
  private isEveryFire(instruction: RenderInstruction | null | undefined): boolean {
    const config = instruction?.render?.config as { frequency?: string } | null | undefined;
    return config?.frequency === 'unlimited';
  }

  /**
   * Get the render instruction for a trigger key.
   * Returns null if:
   *   - No instruction for this trigger
   *   - Already shown this page session (unless the step says every fire)
   *   - Session fatigue limit reached — which gates only a message's FIRST
   *     show: an every-fire step that already spent its slot re-shows
   *     without spending another.
   */
  getInstruction(triggerKey: string): RenderInstruction | null {
    const instruction = this.instructions[triggerKey] ?? null;
    if (!instruction) return null;
    if (this.shown.has(triggerKey)) {
      return this.isEveryFire(instruction) ? instruction : null;
    }
    if (this.shownCount >= this.maxMessages) return null;
    return instruction;
  }

  /** Whether this trigger's message already reached the shopper this session. */
  hasShown(triggerKey: string): boolean {
    return this.shown.has(triggerKey);
  }

  /**
   * Record that a trigger's message actually reached the shopper.
   *
   * "Call after rendering" was already the contract here and the caller broke
   * it, marking the impression before `render.show()` ran. A popup that
   * rendered nothing still spent its slot, and the shopper saw nothing —
   * which also erased the evidence, since the next attempt was suppressed by
   * a message that had never appeared.
   */
  markShown(triggerKey: string): void {
    if (this.shown.has(triggerKey)) return;
    this.shown.add(triggerKey);
    this.shownCount++;
    writeShownState({ keys: [...this.shown], count: this.shownCount });
  }

  /**
   * Find instruction + trigger key by campaign ID.
   * Used when TriggerEngine fires with a campaignId but we need the trigger key.
   */
  findByCampaignId(campaignId: string): { triggerKey: string; instruction: RenderInstruction } | null {
    for (const [key, instr] of Object.entries(this.instructions)) {
      if (instr && instr.campaignId === campaignId) {
        if (this.shown.has(key)) {
          return this.isEveryFire(instr) ? { triggerKey: key, instruction: instr } : null;
        }
        if (this.shownCount >= this.maxMessages) return null;
        return { triggerKey: key, instruction: instr };
      }
    }
    return null;
  }

  /**
   * The message this campaign still has to show, when the session's
   * message limit is what stops it — so the miss can be reported, not lost.
   */
  heldBySessionCap(campaignId: string): RenderInstruction | null {
    if (this.shownCount < this.maxMessages) return null;
    const held = Object.entries(this.instructions).find(
      ([key, instr]) => instr?.campaignId === campaignId && !this.shown.has(key),
    );
    return held?.[1] ?? null;
  }

  /**
   * Get all trigger keys that have instructions.
   */
  getTriggerKeys(): string[] {
    return Object.entries(this.instructions)
      .filter(([, v]) => v !== null)
      .map(([k]) => k);
  }

  /**
   * Get all instructions (for LiveSlots resolution on cart change).
   */
  getAllInstructions(): Record<string, RenderInstruction | null> {
    return this.instructions;
  }

  /**
   * Check if any instructions remain to be shown. An every-fire step that
   * already spent its slot still counts — it can legitimately show again.
   */
  hasInstructions(): boolean {
    return Object.entries(this.instructions).some(([key, v]) => {
      if (v === null) return false;
      if (this.shown.has(key)) return this.isEveryFire(v);
      return this.shownCount < this.maxMessages;
    });
  }
}
